'use client';

import { useTranslations } from 'next-intl';
import { type FC, useMemo, useState } from 'react';
import { BaseLineDownload } from '@/app/components/icons/BaseLineDownload';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLineMarket } from '@/app/components/icons/BaseLineMarket';
import { ProductLineMark } from '@/app/components/icons/ProductLineMark';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { DownloadWatermarkOption } from '@/app/components/shared/DownloadWatermarkOption';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@/app/components/ui/hover-card';
import { SITE } from '@/app/constants';
import { useDevice } from '@/app/hooks';
import { cn } from '@/app/utils';
import { CREATION_TYPE_TO_FILE_EXTENSION } from '../_constants';
import { useBatchDownload } from '../_hooks/use-batch-download';
import { type FileOperationType, getBatchOperations } from '../_utils/file-operations-config';
import type { FileItem } from '../types';

interface BatchOperationsToolbarProps {
  selectedIds: string[];
  clearSelectedItemIds: () => void;
  files: FileItem[];
  isLoading?: FileOperationType | null;
  onOperation: (operation: FileOperationType, items: FileItem[]) => void;
  className?: string;
  isInSearch?: boolean;
}

export const BatchOperationsToolbar: FC<BatchOperationsToolbarProps> = ({
  selectedIds,
  clearSelectedItemIds,
  files,
  isLoading,
  onOperation,
  className,
  isInSearch = false,
}) => {
  const t = useTranslations();
  const batchOperations = getBatchOperations();

  // 获取选中的文件对象
  const selectedFiles = useMemo(
    () => (selectedIds.length ? files.filter((file) => selectedIds.includes(file.id)) : []),
    [files, selectedIds],
  );

  // 检查是否所有选中的文件都已收藏
  const isAllFavorite = useMemo(
    () => selectedFiles.length > 0 && selectedFiles.every((file) => file.is_favorite),
    [selectedFiles],
  );

  // 检查在搜索状态下是否选中了文件夹，如果是则禁用移动操作
  const isMoveDisabled = useMemo(
    () => isInSearch && selectedFiles.some((file) => file.type === 'folder'),
    [isInSearch, selectedFiles],
  );

  const handleOperationClick = (operationType: FileOperationType) => {
    if (selectedFiles.length === 0) return;
    onOperation(operationType, selectedFiles);
  };

  if (selectedIds.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {batchOperations.map((operation) => {
        const isCurrentLoading = isLoading === operation.id;
        const Icon = isCurrentLoading ? BaseLineLoading2 : operation.icon;

        if (operation.id === 'download') {
          return (
            <BatchDownloadButton
              key={operation.id}
              selectedItems={selectedFiles}
              clearSelectedItemIds={clearSelectedItemIds}
              label={t(`creations.operations.${operation.id}`)}
            />
          );
        }

        // 处理收藏操作的特殊逻辑
        if (operation.id === 'mark') {
          const FavoriteIcon = isCurrentLoading ? BaseLineLoading2 : isAllFavorite ? BaseLineMarket : ProductLineMark;
          const favoriteLabel = isAllFavorite ? t('creations.operations.unmark') : t('creations.operations.mark');

          return (
            <Button
              key={operation.id}
              className="flex h-8 items-center gap-2 rounded-8 bg-system-white10 px-3 text-sm"
              onClick={() => handleOperationClick(operation.id)}
              disabled={isCurrentLoading}
            >
              <FavoriteIcon
                className={cn(
                  'text-base',
                  isAllFavorite && !isCurrentLoading && 'text-[#FFCC00]',
                  isCurrentLoading && 'animate-spin',
                )}
              />
              {favoriteLabel}
            </Button>
          );
        }

        // 处理移动操作的特殊逻辑
        if (operation.id === 'move' && isMoveDisabled) {
          return (
            <CustomTooltip
              key={operation.id}
              content={t('creations.operations.moveDisabled')}
              side="top"
              container={document.body}
            >
              <Button
                className={cn(
                  'flex h-8 items-center gap-2 rounded-8 bg-system-white10 px-3 text-sm',
                  'cursor-not-allowed opacity-40',
                )}
                disabled={true}
              >
                <Icon className="text-base" />
                {t(`creations.operations.${operation.id}`)}
              </Button>
            </CustomTooltip>
          );
        }

        return (
          <Button
            key={operation.id}
            className="flex h-8 items-center gap-2 rounded-8 bg-system-white10 px-3 text-sm"
            onClick={() => handleOperationClick(operation.id)}
            disabled={isCurrentLoading}
          >
            <Icon className={cn('text-base', isCurrentLoading && 'animate-spin')} />
            {t(`creations.operations.${operation.id}`)}
          </Button>
        );
      })}
    </div>
  );
};

const BatchDownloadButton = ({
  selectedItems,
  clearSelectedItemIds,
  label,
}: {
  selectedItems: FileItem[];
  clearSelectedItemIds: () => void;
  label: string;
}) => {
  const t = useTranslations();
  const { supportsHover } = useDevice();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [selectedFiles, canChooseWatermark] = useMemo(() => {
    const selectedFiles = selectedItems
      .filter((file) => file.type === 'creation' && file.creationId && file.contentUrl)
      .map((file) => ({
        id: file.creationId || '',
        type: file.creationType || 'unspecified',
        uri: file.contentUrl || '',
        nomark_uri: file.nomark_uri || '',
        fileName: `${file.name}.${CREATION_TYPE_TO_FILE_EXTENSION[file.creationType || 'video']}`,
        hasCopyright: file.hasCopyright,
      }));

    const canChooseWatermark =
      SITE === 'china' ||
      (selectedFiles.length > 0 && selectedFiles.some((item) => ['video', 'image', 'mini_video'].includes(item.type)));
    return [selectedFiles, canChooseWatermark];
  }, [selectedItems]);

  const allHasCopyright = useMemo(() => selectedFiles.every((item) => item.hasCopyright), [selectedFiles]);

  const { isDownloading, isFree, withWatermark, setWithWatermark, handleBatchDownload } = useBatchDownload(
    selectedFiles,
    clearSelectedItemIds, // TODO: 下载完不一定要清空吧？
    allHasCopyright,
  );

  const disabled = selectedFiles.length < selectedItems.length; // 只要包含文件夹就不允许下载

  // 处理下载按钮点击，在不支持 hover 的设备上优先显示对话框
  const handleDownloadClick = () => {
    if (disabled) return;

    // 如果不支持 hover 且可以选择水印，显示对话框
    if (!supportsHover && canChooseWatermark) {
      setDialogOpen(true);
      return;
    }

    // 否则直接下载
    handleBatchDownload();
  };

  // 在对话框中点击下载按钮
  const handleDialogDownload = () => {
    handleBatchDownload();
    setDialogOpen(false);
  };

  const downloadButtonElement = (
    <Button
      className={cn(
        'flex h-8 items-center gap-2 rounded-8 bg-system-white10 px-3 text-sm',
        disabled && 'cursor-not-allowed opacity-40',
      )}
      loadingClassName={'text-system-white'}
      onClick={handleDownloadClick}
      disabled={isDownloading}
      loading={isDownloading}
    >
      <BaseLineDownload className="text-base" />
      {label}
    </Button>
  );

  if (disabled) {
    return (
      <CustomTooltip content={t('creations.operations.downloadDisabled')} side="top" container={document.body}>
        {downloadButtonElement}
      </CustomTooltip>
    );
  }

  if (!canChooseWatermark) {
    return downloadButtonElement;
  }

  // 不支持 hover 的设备使用 Dialog
  if (!supportsHover) {
    return (
      <>
        {downloadButtonElement}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="w-[320px] p-4 sm:gap-0">
            <div className="mb-4 text-center font-semibold text-lg">
              {t('sharedComponent.downloadButton.downloadOptions')}
            </div>
            <DownloadWatermarkOption
              isFree={isFree}
              hasCopyright={allHasCopyright}
              withWatermark={withWatermark}
              setWithWatermark={setWithWatermark}
            />
            <Button variant="primary" className="mt-4 w-full" onClick={handleDialogDownload}>
              {t('creation.action.download')}
            </Button>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // 支持 hover 的设备使用 HoverCard
  return (
    <HoverCard openDelay={0} closeDelay={100}>
      <HoverCardTrigger asChild>{downloadButtonElement}</HoverCardTrigger>
      <HoverCardPortal container={document.body}>
        <HoverCardContent className="w-fit bg-system-bg02 p-1 text-sm" align="center" sideOffset={2} side="top">
          <DownloadWatermarkOption
            isFree={isFree}
            hasCopyright={allHasCopyright}
            withWatermark={withWatermark}
            setWithWatermark={setWithWatermark}
          />
          <HoverCardArrow />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
